import { Button } from "@qn/ui";
import { useQuery } from "@tanstack/react-query";
import { Bot, Mic, MicOff, Phone, PhoneOff, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useCall } from "../lib/call-store";
import { useFeature } from "../lib/features";
import { useT } from "../lib/i18n";
import { AssistantCall } from "./AssistantCall";

/** A soft two-tone ring made with WebAudio (no audio file needed). Returns a stop function. */
function ring(kind: "incoming" | "outgoing") {
  try {
    const ctx = new AudioContext();
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    const osc = ctx.createOscillator();
    osc.frequency.value = kind === "incoming" ? 440 : 425;
    osc.connect(gain);
    osc.start();
    const tick = () => {
      const t = ctx.currentTime;
      gain.gain.cancelScheduledValues(t);
      gain.gain.setValueAtTime(0.06, t);
      gain.gain.setValueAtTime(0, t + 1.0);
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => {
      clearInterval(id);
      osc.stop();
      void ctx.close();
    };
  } catch {
    return () => undefined;
  }
}

type Phase = "ringing" | "connecting" | "live" | "ended";
interface Signal {
  id: number;
  kind: "offer" | "answer" | "ice";
  payload: string;
}

/** The call itself: microphone → WebRTC peer connection → the other person, with polling for signalling. */
function CallScreen({
  id,
  role,
  name,
  onDone,
}: {
  id: string;
  role: "caller" | "callee";
  name: string;
  onDone: () => void;
}) {
  const t = useT();
  const [phase, setPhase] = useState<Phase>(role === "caller" ? "ringing" : "connecting");
  const [muted, setMuted] = useState(false);
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const phaseRef = useRef<Phase>(phase);
  phaseRef.current = phase;

  // biome-ignore lint/correctness/useExhaustiveDependencies: one call session per mount
  useEffect(() => {
    let closed = false;
    let after = 0;
    let remoteSet = false;
    const pendingIce: RTCIceCandidateInit[] = [];
    const post = (kind: string, payload: unknown) =>
      api(`/assistant/calls/${id}/signal`, {
        method: "POST",
        body: { kind, payload: JSON.stringify(payload) },
      }).catch(() => undefined);
    const stopRing = role === "caller" ? ring("outgoing") : () => undefined;
    let ringStopped = false;
    const quiet = () => {
      if (!ringStopped) {
        ringStopped = true;
        stopRing();
      }
    };

    (async () => {
      try {
        const ice = await api<{ iceServers: RTCIceServer[] }>("/assistant/calls/ice");
        const mic = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        if (closed) {
          for (const x of mic.getTracks()) x.stop();
          return;
        }
        micRef.current = mic;
        const pc = new RTCPeerConnection({ iceServers: ice.iceServers });
        pcRef.current = pc;
        for (const tr of mic.getTracks()) pc.addTrack(tr, mic);
        pc.ontrack = (e) => {
          if (audio.current) {
            audio.current.srcObject = e.streams[0] ?? null;
            void audio.current.play().catch(() => undefined);
          }
        };
        pc.onicecandidate = (e) => e.candidate && void post("ice", e.candidate.toJSON());
        pc.onconnectionstatechange = () => {
          if (pc.connectionState === "connected") {
            quiet();
            setPhase("live");
          }
          if (pc.connectionState === "failed") setErr(t("call.failed"));
        };
        if (role === "caller") {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          await post("offer", offer);
        }
        const handle = async (s: Signal) => {
          const data = JSON.parse(s.payload) as RTCSessionDescriptionInit & RTCIceCandidateInit;
          if (s.kind === "offer" && role === "callee") {
            await pc.setRemoteDescription(data);
            remoteSet = true;
            for (const c of pendingIce.splice(0))
              await pc.addIceCandidate(c).catch(() => undefined);
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            await post("answer", answer);
          } else if (s.kind === "answer" && role === "caller") {
            await pc.setRemoteDescription(data);
            remoteSet = true;
            for (const c of pendingIce.splice(0))
              await pc.addIceCandidate(c).catch(() => undefined);
          } else if (s.kind === "ice") {
            if (remoteSet) await pc.addIceCandidate(data).catch(() => undefined);
            else pendingIce.push(data);
          }
        };
        while (!closed) {
          const r = await api<{ state: string; signals: Signal[] }>(
            `/assistant/calls/${id}?after=${after}`,
          ).catch(() => null);
          if (r) {
            for (const s of r.signals) {
              after = Math.max(after, s.id);
              await handle(s);
            }
            if (["ended", "declined", "missed"].includes(r.state)) {
              quiet();
              setPhase("ended");
              if (r.state === "declined") setErr(t("call.declined"));
              if (r.state === "missed") setErr(t("call.noAnswer"));
              break;
            }
            if (r.state === "active" && phaseRef.current === "ringing") {
              quiet();
              setPhase("connecting");
            }
          }
          await new Promise((res) => setTimeout(res, 900));
        }
      } catch (e) {
        setErr(
          e instanceof Error && e.name === "NotAllowedError"
            ? t("call.micDenied")
            : e instanceof Error
              ? e.message
              : String(e),
        );
        setPhase("ended");
      }
    })();
    return () => {
      closed = true;
      quiet();
      pcRef.current?.close();
      for (const x of micRef.current?.getTracks() ?? []) x.stop();
      void api(`/assistant/calls/${id}/end`, { method: "POST", body: {} }).catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    if (phase !== "live") return;
    const i = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(i);
  }, [phase]);
  useEffect(() => {
    if (phase !== "ended") return;
    const i = setTimeout(onDone, err ? 3500 : 1200);
    return () => clearTimeout(i);
  }, [phase, err, onDone]);

  const status =
    phase === "ringing"
      ? t("call.ringing")
      : phase === "connecting"
        ? t("call.connecting")
        : phase === "live"
          ? `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`
          : t("call.ended");
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("call.title")}
      className="fixed inset-0 z-[80] flex flex-col items-center justify-between bg-[var(--bg)]/95 p-8 backdrop-blur-xl"
    >
      {/* Live call audio only: there is nothing to caption (the other person's voice is the content). */}
      {/* biome-ignore lint/a11y/useMediaCaption: live voice call */}
      <audio ref={audio} autoPlay playsInline />
      <div className="mt-10 text-center">
        <div
          className="mx-auto grid size-28 place-items-center rounded-full bg-gradient-to-br from-[var(--accent)] to-[var(--accent-2)] text-4xl font-semibold text-[var(--accent-fg)]"
          aria-hidden
        >
          {name.slice(0, 1).toUpperCase()}
        </div>
        <h2 className="mt-5 font-display text-2xl font-semibold" dir="auto">
          {name}
        </h2>
        <p role="status" aria-live="polite" className="mt-1 text-[var(--fg-muted)]">
          {status}
        </p>
        {err && (
          <p role="alert" className="mt-3 text-sm text-[var(--danger)]" dir="auto">
            {err}
          </p>
        )}
      </div>
      <div className="mb-8 flex gap-4">
        <Button
          variant="outline"
          size="lg"
          aria-pressed={muted}
          onClick={() => {
            const next = !muted;
            setMuted(next);
            for (const tr of micRef.current?.getAudioTracks() ?? []) tr.enabled = !next;
          }}
        >
          {muted ? <MicOff className="size-5" /> : <Mic className="size-5" />}{" "}
          {muted ? t("voice.unmute") : t("voice.mute")}
        </Button>
        <Button variant="danger" size="lg" onClick={onDone}>
          <PhoneOff className="size-5" /> {t("call.hangup")}
        </Button>
      </div>
    </div>
  );
}

interface Ringing {
  id: string;
  from_name: string;
  kind?: "person" | "assistant";
  thread_id?: string | null;
}

/** Makes a ring hard to miss: vibration, a flashing tab title and (if allowed) a system notification while the tab is hidden. */
function useRingEffects(active: boolean, label: string) {
  useEffect(() => {
    if (!active) return;
    const title = document.title;
    let on = false;
    const flash = setInterval(() => {
      on = !on;
      document.title = on ? `📞 ${label}` : title;
    }, 900);
    const buzz = () => {
      try {
        navigator.vibrate?.([400, 200, 400]);
      } catch {
        /* not supported */
      }
    };
    buzz();
    const vib = setInterval(buzz, 3000);
    let note: Notification | undefined;
    try {
      if (document.hidden && "Notification" in window && Notification.permission === "granted")
        note = new Notification(label, { tag: "qn-call", requireInteraction: true });
    } catch {
      /* optional */
    }
    return () => {
      clearInterval(flash);
      clearInterval(vib);
      document.title = title;
      try {
        navigator.vibrate?.(0);
      } catch {
        /* ignore */
      }
      note?.close();
    };
  }, [active, label]);
}

/** Lives in the app shell: shows incoming calls (polling) and the active call screen. */
export function CallHost() {
  const t = useT();
  const { call, error, answer, clear } = useCall();
  const assistantOn = useFeature("assistant");
  const [assistantCall, setAssistantCall] = useState<{ id: string; threadId: string } | null>(null);
  const incoming = useQuery({
    queryKey: ["calls-incoming"],
    queryFn: () => api<{ calls: Ringing[] }>("/assistant/calls/incoming"),
    refetchInterval: call || assistantCall ? false : 4000,
    retry: false,
    enabled: assistantOn && !call && !assistantCall,
  });
  const ringing = !call && !assistantCall ? incoming.data?.calls[0] : undefined;
  const [dismissed, setDismissed] = useState<string | null>(null);
  const visible = ringing && dismissed !== ringing.id ? ringing : undefined;
  const isAssistant = visible?.kind === "assistant";
  const who = visible
    ? isAssistant
      ? t("call.assistantOf", { name: visible.from_name })
      : visible.from_name
    : "";
  useEffect(() => {
    if (!visible) return;
    return ring("incoming");
  }, [visible]);
  useRingEffects(!!visible, who);

  const decline = (r: Ringing) => {
    setDismissed(r.id);
    void api(`/assistant/calls/${r.id}/decline`, { method: "POST", body: {} }).catch(
      () => undefined,
    );
  };
  const accept = async (r: Ringing) => {
    if (r.kind === "assistant" && r.thread_id) {
      try {
        await api(`/assistant/calls/${r.id}/accept`, { method: "POST", body: {} });
        setAssistantCall({ id: r.id, threadId: r.thread_id });
      } catch (e) {
        useCall.setState({ error: e instanceof Error ? e.message : String(e) });
      }
      return;
    }
    await answer(r.id, r.from_name);
  };

  return (
    <>
      {call && (
        <CallScreen key={call.id} id={call.id} role={call.role} name={call.name} onDone={clear} />
      )}
      {assistantCall && (
        <AssistantCall
          threadId={assistantCall.threadId}
          onClose={() => {
            void api(`/assistant/calls/${assistantCall.id}/end`, {
              method: "POST",
              body: {},
            }).catch(() => undefined);
            setAssistantCall(null);
          }}
        />
      )}
      {visible && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div
            role="alertdialog"
            aria-label={t("call.incoming")}
            aria-live="assertive"
            className="w-[min(92vw,22rem)] rounded-3xl border border-[var(--line-strong)] bg-[var(--surface-2)] p-6 text-center shadow-2xl"
          >
            <div className="relative mx-auto mb-4 grid size-20 place-items-center">
              <span className="absolute inset-0 animate-ping rounded-full bg-[var(--accent)]/30" />
              <span className="relative grid size-20 place-items-center rounded-full bg-[var(--accent)] text-[var(--accent-fg,#fff)]">
                {isAssistant ? <Bot className="size-9" /> : <Phone className="size-9" />}
              </span>
            </div>
            <p className="text-xs text-[var(--fg-muted)]">
              {isAssistant ? t("call.assistantCalling") : t("call.incoming")}
            </p>
            <p className="mt-1 font-display text-xl font-semibold" dir="auto">
              {who}
            </p>
            <div className="mt-6 flex justify-center gap-6">
              <Button
                variant="danger"
                size="icon"
                className="size-14 rounded-full"
                aria-label={t("call.decline")}
                onClick={() => decline(visible)}
              >
                <PhoneOff className="size-6" />
              </Button>
              <Button
                variant="primary"
                size="icon"
                className="size-14 rounded-full"
                aria-label={t("call.answer")}
                onClick={() => void accept(visible)}
              >
                <Phone className="size-6" />
              </Button>
            </div>
          </div>
        </div>
      )}
      {assistantOn && <AssistantPopups suppress={!!visible || !!call || !!assistantCall} />}
      {error && (
        <div
          role="alert"
          className="fixed inset-x-0 top-4 z-[90] mx-auto flex w-[min(92vw,26rem)] items-center justify-between gap-3 rounded-xl border border-[var(--danger)] bg-[var(--surface-2)] p-3 text-sm text-[var(--danger)]"
        >
          <span dir="auto">{error}</span>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("artifact.close")}
            onClick={() => useCall.setState({ error: null })}
          >
            ×
          </Button>
        </div>
      )}
    </>
  );
}

interface InboxThread {
  id: string;
  from_name: string;
  status: string;
  voice: number;
  last_text?: string | null;
  last_sender?: string | null;
  updated_at: number;
}

/**
 * Someone's assistant wrote to you: a card pops up with its message and one-tap answers
 * ("yes I'm free" / "later" / "busy") so you can reply immediately, or type your own.
 */
function AssistantPopups({ suppress }: { suppress: boolean }) {
  const t = useT();
  const list = useQuery({
    queryKey: ["inbox-popup"],
    queryFn: () => api<{ threads: InboxThread[] }>("/assistant/inbox"),
    refetchInterval: 10_000,
    retry: false,
  });
  const [seen, setSeen] = useState<Record<string, number>>({});
  const [reply, setReply] = useState<Record<string, string>>({});
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const fresh = (list.data?.threads ?? []).find(
    (x) =>
      x.voice !== 1 &&
      x.last_sender === "assistant" &&
      (x.status === "waiting" || x.status === "replied") &&
      Date.now() - x.updated_at < 10 * 60_000 &&
      (seen[x.id] ?? 0) < x.updated_at,
  );
  useRingEffects(
    !!fresh && !suppress,
    fresh ? t("call.assistantOf", { name: fresh.from_name }) : "",
  );
  if (!fresh || suppress) return null;
  const done = () => setSeen((s) => ({ ...s, [fresh.id]: Number.MAX_SAFE_INTEGER }));
  const send = async (msg: string) => {
    if (!msg.trim() || busy) return;
    setBusy(true);
    setFailed(null);
    try {
      const r = await api<{ reply?: string; done: boolean }>(`/assistant/inbox/${fresh.id}/reply`, {
        method: "POST",
        body: { message: msg },
      });
      setReply((m) => ({ ...m, [fresh.id]: r.reply ?? "" }));
      setText("");
      if (r.done) setTimeout(done, 6000);
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const quick = [t("popup.quick.free"), t("popup.quick.later"), t("popup.quick.busy")];
  return (
    <div
      role="alertdialog"
      aria-label={t("call.assistantOf", { name: fresh.from_name })}
      className="fixed inset-x-0 top-14 z-[85] mx-auto w-[min(94vw,26rem)] rounded-2xl border border-[var(--line-strong)] bg-[var(--surface-2)] p-4 shadow-2xl"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold" dir="auto">
          <Bot className="size-4 text-[var(--accent)]" />
          {t("call.assistantOf", { name: fresh.from_name })}
        </p>
        <Button variant="ghost" size="icon" aria-label={t("artifact.close")} onClick={done}>
          <X className="size-4" />
        </Button>
      </div>
      <p className="mt-2 text-sm" dir="auto">
        {fresh.last_text}
      </p>
      {reply[fresh.id] && (
        <p
          className="mt-2 rounded-xl bg-[var(--surface-3)] p-2 text-sm"
          dir="auto"
          aria-live="polite"
        >
          {reply[fresh.id]}
        </p>
      )}
      {busy && (
        <p className="mt-2 text-xs text-[var(--fg-muted)]" role="status">
          {t("popup.thinking")}
        </p>
      )}
      {failed && (
        <p role="alert" className="mt-2 text-xs text-[var(--danger)]">
          {failed}
        </p>
      )}
      {!reply[fresh.id] && (
        <div className="mt-3 flex flex-wrap gap-2">
          {quick.map((q) => (
            <Button
              key={q}
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void send(q)}
            >
              {q}
            </Button>
          ))}
        </div>
      )}
      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void send(text);
        }}
      >
        <input
          className="min-w-0 flex-1 rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-sm"
          dir="auto"
          value={text}
          aria-label={t("assistant.write")}
          placeholder={t("assistant.write")}
          onChange={(e) => setText(e.target.value)}
        />
        <Button type="submit" size="sm" loading={busy} disabled={!text.trim()}>
          {t("assistant.send")}
        </Button>
      </form>
    </div>
  );
}
